import { longDataJobCreateSchema, longDataJobIdSchema, longDataJobPath, longDataJobDownloadPath, type LongDataJob } from '@es-tools/governed-query/longDataJobs';

export const LONG_DATA_JOB_ROUTE = '/data/jobs/:id';
export const LONG_DATA_JOBS_API = '/api/data-explorer/jobs';
export type LongJobClientOptions = { baseUrl?: string; fetchImpl?: typeof fetch; signal?: AbortSignal };
export class LongDataJobRequestError extends Error {
  constructor(message: string, readonly status: number) { super(message); this.name = 'LongDataJobRequestError'; }
}
const apiBase = (options: LongJobClientOptions) => options.baseUrl?.replace(/\/+$/, '') ?? `${(import.meta.env?.VITE_AI_CENTER_AGENT_API_BASE_URL ?? import.meta.env?.VITE_AGENT_API_BASE_URL ?? '').replace(/\/+$/, '')}${LONG_DATA_JOBS_API}`;
const fail = async (response: Response): Promise<never> => {
  const body = await response.json().catch(() => null);
  throw new LongDataJobRequestError(typeof body?.message === 'string' ? body.message : `Collection request unavailable (${response.status}).`, response.status);
};
const jobRequest = async (id: string, cancel: boolean, options: LongJobClientOptions): Promise<LongDataJob> => {
  const response = await (options.fetchImpl ?? fetch)(`${apiBase(options)}/${longDataJobIdSchema.parse(id)}${cancel ? '/cancel' : ''}`, {
    method: cancel ? 'POST' : 'GET', credentials: 'include', cache: 'no-store', signal: options.signal,
    headers: { accept: 'application/json' },
  });
  if (!response.ok) return fail(response);
  const payload = await response.json();
  if (payload?.ok !== true || payload?.job?.id !== id || payload?.job?.schema !== 'marsmen.long-data-job.v1') throw new LongDataJobRequestError('The collection response did not match this job.', 502);
  return payload.job as LongDataJob;
};
export const getLongDataJob = (id: string, options: LongJobClientOptions = {}) => jobRequest(id, false, options);
export const cancelLongDataJob = (id: string, options: LongJobClientOptions = {}) => jobRequest(id, true, options);
/** Adopts one existing owner execution; it never submits SQL or creates a query. */
export const createLongDataJob = async (queryExecutionId: string, options: LongJobClientOptions = {}): Promise<LongDataJob> => {
  const input = longDataJobCreateSchema.parse({ queryExecutionId });
  const response = await (options.fetchImpl ?? fetch)(apiBase(options), {
    method: 'POST', credentials: 'include', cache: 'no-store', signal: options.signal,
    headers: { accept: 'application/json', 'content-type': 'application/json' }, body: JSON.stringify(input),
  });
  if (!response.ok) return fail(response);
  const payload = await response.json();
  const job = payload?.job;
  const parsedId = longDataJobIdSchema.safeParse(job?.id);
  if (payload?.ok !== true || job?.schema !== 'marsmen.long-data-job.v1' || !parsedId.success || job.queryExecutionId !== queryExecutionId || job.path !== longDataJobPath(parsedId.data)
    || !['queued','collecting','ready','failed','cancelled','expired','revoked'].includes(job.status)) throw new LongDataJobRequestError('The collection response did not match this query or private job link.', 502);
  return job as LongDataJob;
};
export const longJobCanDownload = (job: LongDataJob, now = Date.now()): boolean => job.status === 'ready' && Date.parse(job.expiresAt) > now && job.download.available && job.download.path === longDataJobDownloadPath(job.id);
export const downloadLongDataJob = async (job: LongDataJob, options: LongJobClientOptions = {}): Promise<Blob> => {
  if (!longJobCanDownload(job)) throw new LongDataJobRequestError('A download is not available for this collection.', 409);
  const response = await (options.fetchImpl ?? fetch)(`${apiBase(options)}/${longDataJobIdSchema.parse(job.id)}/download.csv`, {
    method: 'GET', credentials: 'include', cache: 'no-store', signal: options.signal, headers: { accept: 'text/csv' },
  });
  if (!response.ok) return fail(response);
  if (response.headers.get('content-type')?.split(';')[0].trim().toLowerCase() !== 'text/csv') throw new LongDataJobRequestError('The collection response was not a CSV.', 502);
  return response.blob();
};
const COLLECTION_LIMIT_LABELS = new Map<string, string>([
  ['row_limit', 'Row limit reached'],
  ['byte_limit', 'Collection size limit reached'],
  ['row_size_limit', 'An individual row was too large to collect'],
  ['page_limit', 'Bounded page collection limit reached'],
  ['transaction_page_limit', 'Collection storage limit reached'],
  ['chunk_limit', 'Collection part limit reached'],
]);
/** Internal bounds are a closed display vocabulary, never arbitrary server diagnostics. */
export const longJobPartialReasonLabel = (reason: string | null | undefined): string =>
  (reason ? COLLECTION_LIMIT_LABELS.get(reason) : undefined) ?? 'Bounded collection limit reached';
export const longJobCoverageLabel = (job: Pick<LongDataJob, 'status' | 'coverage'>): string => {
  const { returnedRows, totalRows, truncated, reason } = job.coverage;
  const rows = `${returnedRows.toLocaleString('en-US')} collected rows`;
  if (truncated) return `${rows} · partial result${totalRows === null ? '' : ` of ${totalRows.toLocaleString('en-US')}`} · ${longJobPartialReasonLabel(reason)}`;
  if (job.status !== 'ready') return `${rows} · collection ${job.status === 'collecting' || job.status === 'queued' ? 'in progress' : 'incomplete'}`;
  if (totalRows === null) return `${rows} · total coverage unknown`;
  return `${rows} of ${totalRows.toLocaleString('en-US')}${returnedRows === totalRows ? ' · complete result' : ' · incomplete result'}`;
};
